import React, { useEffect, useState, useRef, Component } from 'react';
import { ChevronLeft, ChevronRight, AlertTriangle, Check, Calendar as CalendarIcon, X, User, Mail, Phone, MapPin, Users, CreditCard, Home, ExternalLink, GripVertical, Lock, Plus, Sparkles, List } from 'lucide-react';
import { MobileLayout } from '../components/MobileLayout';
import { NuovoInterventoWizard } from '../components/NuovoInterventoWizard';
// Types for the Gantt Chart
type EventType = 'checkout' | 'checkin' | 'cleaning-suggested' | 'cleaning-confirmed' | 'external';
interface CalendarEvent {
  id: string;
  type: EventType;
  timeStart: string;
  timeEnd?: string;
  label: string;
  details?: string;
  source?: 'google' | 'outlook'; // For external events
}
interface DaySchedule {
  date: string;
  dayName: string;
  dayNumber: string;
  events: CalendarEvent[];
  isToday?: boolean;
}
export function CalendarioPage() {
  const [viewMode, setViewMode] = useState<'week' | 'list'>(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      return 'list';
    }
    return 'week';
  });
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<CalendarEvent | null>(null);
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const [isOutlookModalOpen, setIsOutlookModalOpen] = useState(false);
  // Drag & Resize state
  const [draggedEvent, setDraggedEvent] = useState<string | null>(null);
  const [resizingEvent, setResizingEvent] = useState<string | null>(null);
  const [resizeStartY, setResizeStartY] = useState<number>(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  // Integration states
  const [integrations, setIntegrations] = useState({
    google: true,
    outlook: false
  });
  // Nuovo intervento wizard
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  // Pointer-based drag state (replaces HTML5 DnD)
  const timelineRef = useRef<HTMLDivElement>(null);
  type ActiveDrag = {
    eventId: string;
    sourceDayIndex: number;
    durationMinutes: number;
    pointerStartX: number;
    pointerStartY: number;
    pointerX: number;
    pointerY: number;
    hasMoved: boolean;
    targetDayIndex: number | null;
    targetStartMinutes: number | null;
  };
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  // Helper: convert a clientX/clientY to (dayIndex, snapped start minutes from 8:00)
  const getTargetFromPointer = (clientX: number, clientY: number, durationMinutes: number): {
    dayIndex: number | null;
    startMinutes: number | null;
  } => {
    const el = timelineRef.current;
    if (!el) return {
      dayIndex: null,
      startMinutes: null
    };
    const rect = el.getBoundingClientRect();
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;
    if (relX < 0 || relX > rect.width || relY < 0 || relY > rect.height) {
      return {
        dayIndex: null,
        startMinutes: null
      };
    }
    const dayIndex = Math.min(13, Math.max(0, Math.floor(relX / rect.width * 14)));
    const minutesFromTop = relY / rect.height * 720;
    // Snap start to 30-min, ensure event fits within 8:00-20:00
    const snappedStart = snapTo30Minutes(minutesFromTop);
    const maxStart = 720 - durationMinutes;
    const clampedStart = Math.max(0, Math.min(maxStart, snappedStart));
    return {
      dayIndex,
      startMinutes: clampedStart
    };
  };
  // Mock Google accounts with calendar selection
  const [googleAccounts, setGoogleAccounts] = useState([{
    id: '1',
    email: 'mario.rossi@gmail.com',
    calendars: [{
      id: 'cal1',
      name: 'Personale',
      selected: true
    }, {
      id: 'cal2',
      name: 'Lavoro',
      selected: true
    }, {
      id: 'cal3',
      name: 'Villa Sunset',
      selected: false
    }, {
      id: 'cal4',
      name: 'Famiglia',
      selected: false
    }]
  }, {
    id: '2',
    email: 'mario.rossi.work@gmail.com',
    calendars: [{
      id: 'cal5',
      name: 'Riunioni',
      selected: true
    }, {
      id: 'cal6',
      name: 'Progetti',
      selected: false
    }]
  }]);
  // Mock Outlook accounts with calendar selection
  const [outlookAccounts, setOutlookAccounts] = useState([{
    id: '1',
    email: 'mario.rossi@outlook.com',
    calendars: [{
      id: 'cal1',
      name: 'Calendario',
      selected: true
    }, {
      id: 'cal2',
      name: 'Compleanni',
      selected: false
    }]
  }]);
  // Mock Data - Now as state to make it mutable - EXTENDED TO 14 DAYS WITH MORE EVENTS
  const [weekData, setWeekData] = useState<DaySchedule[]>([{
    date: '2024-01-15',
    dayName: 'Lun',
    dayNumber: '15',
    events: [{
      id: '16',
      type: 'checkout',
      timeStart: '10:00',
      label: 'Roberto F.'
    }, {
      id: '17',
      type: 'cleaning-suggested',
      timeStart: '11:00',
      timeEnd: '15:00',
      label: 'Pulizia'
    }, {
      id: '18',
      type: 'checkin',
      timeStart: '15:00',
      label: 'Francesca N.'
    }]
  }, {
    date: '2024-01-16',
    dayName: 'Mar',
    dayNumber: '16',
    events: [{
      id: '19',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Luca P.'
    }, {
      id: '20',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '16:00',
      label: 'Pulizia'
    }, {
      id: '21',
      type: 'checkin',
      timeStart: '16:00',
      label: 'Giulia S.'
    }]
  }, {
    date: '2024-01-17',
    dayName: 'Mer',
    dayNumber: '17',
    events: [{
      id: 'ext-1',
      type: 'external',
      timeStart: '09:00',
      timeEnd: '10:00',
      label: 'Team meeting',
      source: 'google'
    }]
  }, {
    date: '2024-01-18',
    dayName: 'Gio',
    dayNumber: '18',
    events: [{
      id: '22',
      type: 'checkout',
      timeStart: '10:00',
      label: 'Francesca N.'
    }, {
      id: '23',
      type: 'cleaning-suggested',
      timeStart: '11:00',
      timeEnd: '14:00',
      label: 'Pulizia'
    }, {
      id: '24',
      type: 'checkin',
      timeStart: '14:00',
      label: 'Davide C.'
    }]
  }, {
    date: '2024-01-19',
    dayName: 'Ven',
    dayNumber: '19',
    events: [{
      id: '4',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Sofia L.'
    }, {
      id: '5',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '14:30',
      label: 'Pulizia (Stretto!)'
    }, {
      id: '6',
      type: 'checkin',
      timeStart: '14:30',
      label: 'Paolo V.'
    }, {
      id: 'ext-2',
      type: 'external',
      timeStart: '16:00',
      timeEnd: '17:00',
      label: 'Call cliente',
      source: 'outlook'
    }]
  }, {
    date: '2024-01-20',
    dayName: 'Sab',
    dayNumber: '20',
    events: []
  }, {
    date: '2024-01-21',
    dayName: 'Dom',
    dayNumber: '21',
    events: []
  }, {
    date: '2024-01-22',
    dayName: 'Lun',
    dayNumber: '22',
    events: [{
      id: '28',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Paolo V.'
    }, {
      id: '29',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '15:00',
      label: 'Pulizia'
    }, {
      id: '30',
      type: 'checkin',
      timeStart: '15:00',
      label: 'Chiara B.'
    }]
  }, {
    date: '2024-01-23',
    dayName: 'Mar',
    dayNumber: '23',
    events: [{
      id: '31',
      type: 'checkout',
      timeStart: '10:00',
      label: 'Davide C.'
    }, {
      id: '32',
      type: 'cleaning-suggested',
      timeStart: '11:00',
      timeEnd: '16:00',
      label: 'Pulizia'
    }, {
      id: '33',
      type: 'checkin',
      timeStart: '16:00',
      label: 'Valentina R.'
    }]
  }, {
    date: '2024-01-24',
    dayName: 'Mer',
    dayNumber: '24',
    events: []
  }, {
    date: '2024-01-25',
    dayName: 'Gio',
    dayNumber: '25',
    events: [{
      id: '34',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Elena T.'
    }, {
      id: '35',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '14:30',
      label: 'Pulizia (Stretto!)'
    }, {
      id: '36',
      type: 'checkin',
      timeStart: '14:30',
      label: 'Simone G.'
    }]
  }, {
    date: '2024-01-26',
    dayName: 'Ven',
    dayNumber: '26',
    events: [{
      id: '37',
      type: 'checkout',
      timeStart: '10:00',
      label: 'Andrea M.'
    }, {
      id: '38',
      type: 'cleaning-suggested',
      timeStart: '11:00',
      timeEnd: '15:00',
      label: 'Pulizia'
    }, {
      id: '39',
      type: 'checkin',
      timeStart: '15:00',
      label: 'Martina L.'
    }]
  }, {
    date: '2024-01-27',
    dayName: 'Sab',
    dayNumber: '27',
    events: [{
      id: '13',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Laura M.'
    }, {
      id: '14',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '16:00',
      label: 'Pulizia'
    }, {
      id: '15',
      type: 'checkin',
      timeStart: '16:00',
      label: 'Giuseppe P.'
    }]
  }, {
    date: '2024-01-28',
    dayName: 'Dom',
    dayNumber: '28',
    events: [{
      id: '40',
      type: 'checkout',
      timeStart: '11:00',
      label: 'Chiara B.'
    }, {
      id: '41',
      type: 'cleaning-suggested',
      timeStart: '12:00',
      timeEnd: '16:00',
      label: 'Pulizia'
    }, {
      id: '42',
      type: 'checkin',
      timeStart: '16:00',
      label: 'Federico T.'
    }]
  }]);
  // Helper: Convert minutes to time string
  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };
  // Helper: Convert time string to minutes
  const timeToMinutes = (time: string): number => {
    const [hours, mins] = time.split(':').map(Number);
    return hours * 60 + mins;
  };
  // Helper: Snap to 30-minute intervals
  const snapTo30Minutes = (minutes: number): number => {
    return Math.round(minutes / 30) * 30;
  };
  // Helper: Check for conflicts with fixed events
  const hasConflict = (dayIndex: number, startTime: string, endTime: string, excludeEventId: string): boolean => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    const dayEvents = weekData[dayIndex].events.filter(e => e.id !== excludeEventId);
    for (const event of dayEvents) {
      if (event.type === 'checkin' || event.type === 'checkout') {
        const eventMinutes = timeToMinutes(event.timeStart);
        if (event.type === 'checkout') {
          // Cleaning must START at or after checkout time
          if (startMinutes < eventMinutes) {
            return true; // Conflict: cleaning starts before checkout
          }
        }
        if (event.type === 'checkin') {
          // Cleaning must END at or before checkin time
          if (endMinutes > eventMinutes) {
            return true; // Conflict: cleaning ends after checkin
          }
        }
      }
    }
    return false;
  };
  // Pointer-based drag — applies a drop at (targetDayIndex, targetStartMinutes from 8:00)
  const applyPointerDrop = (eventId: string, sourceDayIndex: number, targetDayIndex: number, targetStartMinutesFrom8: number) => {
    const snappedMinutes = snapTo30Minutes(targetStartMinutesFrom8 + 480); // 480 = 8:00 AM
    let newStartTime = minutesToTime(snappedMinutes);
    // Find the event
    let event: CalendarEvent | undefined;
    let duration = 60; // default 1 hour
    for (const day of weekData) {
      const found = day.events.find(e => e.id === eventId);
      if (found) {
        event = found;
        if (found.timeEnd) {
          duration = timeToMinutes(found.timeEnd) - timeToMinutes(found.timeStart);
        }
        break;
      }
    }
    if (!event) return;
    let newEndTime = minutesToTime(snappedMinutes + duration);
    // Find checkout and checkin times for this day
    const dayEvents = weekData[targetDayIndex].events;
    const checkout = dayEvents.find(e => e.type === 'checkout');
    const checkin = dayEvents.find(e => e.type === 'checkin');
    let adjustedStartTime = newStartTime;
    let adjustedEndTime = newEndTime;
    // If there's a checkout, ensure we start at or after it
    if (checkout) {
      const checkoutMinutes = timeToMinutes(checkout.timeStart);
      const startMinutes = timeToMinutes(newStartTime);
      if (startMinutes < checkoutMinutes) {
        // Snap to checkout time
        adjustedStartTime = checkout.timeStart;
        adjustedEndTime = minutesToTime(checkoutMinutes + duration);
      }
    }
    // If there's a checkin, ensure we end at or before it
    if (checkin) {
      const checkinMinutes = timeToMinutes(checkin.timeStart);
      const endMinutes = timeToMinutes(adjustedEndTime);
      if (endMinutes > checkinMinutes) {
        // Snap end to checkin time
        adjustedEndTime = checkin.timeStart;
        // Adjust start if duration doesn't fit
        const newStartMinutes = checkinMinutes - duration;
        if (checkout && newStartMinutes < timeToMinutes(checkout.timeStart)) {
          // Not enough space - show error
          setConflictMessage("Non c'è abbastanza spazio per questo intervento");
          setShowConflictWarning(true);
          setTimeout(() => setShowConflictWarning(false), 3000);
          setDraggedEvent(null);
          return;
        }
        adjustedStartTime = minutesToTime(Math.max(480, newStartMinutes)); // 480 = 8:00
      }
    }
    // Use adjusted times for conflict check
    if (hasConflict(targetDayIndex, adjustedStartTime, adjustedEndTime, eventId)) {
      setConflictMessage('Impossibile spostare qui: sovrapposizione con check-in/check-out');
      setShowConflictWarning(true);
      setTimeout(() => setShowConflictWarning(false), 3000);
      setDraggedEvent(null);
      return;
    }
    // Update event position with adjusted times
    setWeekData(prevData => {
      const newData = [...prevData];
      // Remove from source day
      newData[sourceDayIndex] = {
        ...newData[sourceDayIndex],
        events: newData[sourceDayIndex].events.filter(e => e.id !== eventId)
      };
      // Add to target day with adjusted time
      const updatedEvent = {
        ...event!,
        timeStart: adjustedStartTime,
        timeEnd: adjustedEndTime
      };
      newData[targetDayIndex] = {
        ...newData[targetDayIndex],
        events: [...newData[targetDayIndex].events, updatedEvent]
      };
      return newData;
    });
    setConfirmationMessage(`Intervento spostato alle ${adjustedStartTime}`);
    setShowConfirmation(true);
    setTimeout(() => setShowConfirmation(false), 3000);
    setDraggedEvent(null);
  };
  // Start a pointer-based drag on a cleaning event
  const handleEventPointerDown = (e: React.PointerEvent<HTMLDivElement>, eventId: string, sourceDayIndex: number, durationMinutes: number) => {
    // Only primary button / single touch
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    setActiveDrag({
      eventId,
      sourceDayIndex,
      durationMinutes,
      pointerStartX: e.clientX,
      pointerStartY: e.clientY,
      pointerX: e.clientX,
      pointerY: e.clientY,
      hasMoved: false,
      targetDayIndex: sourceDayIndex,
      targetStartMinutes: null
    });
  };
  // Effect: wire window-level pointermove/pointerup while a drag is active
  useEffect(() => {
    if (!activeDrag) return;
    const DRAG_THRESHOLD = 5;
    const handleMove = (ev: PointerEvent) => {
      setActiveDrag(prev => {
        if (!prev) return prev;
        const dx = ev.clientX - prev.pointerStartX;
        const dy = ev.clientY - prev.pointerStartY;
        const hasMoved = prev.hasMoved || Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD;
        const target = hasMoved ? getTargetFromPointer(ev.clientX, ev.clientY, prev.durationMinutes) : {
          dayIndex: prev.targetDayIndex,
          startMinutes: prev.targetStartMinutes
        };
        if (hasMoved && !prev.hasMoved) {
          setDraggedEvent(prev.eventId);
        }
        return {
          ...prev,
          pointerX: ev.clientX,
          pointerY: ev.clientY,
          hasMoved,
          targetDayIndex: target.dayIndex,
          targetStartMinutes: target.startMinutes
        };
      });
    };
    const handleUp = (ev: PointerEvent) => {
      setActiveDrag(prev => {
        if (!prev) return null;
        if (!prev.hasMoved) {
          // Treat as a click: open the wizard
          setIsWizardOpen(true);
        } else if (prev.targetDayIndex !== null && prev.targetStartMinutes !== null) {
          applyPointerDrop(prev.eventId, prev.sourceDayIndex, prev.targetDayIndex, prev.targetStartMinutes);
        }
        setDraggedEvent(null);
        return null;
      });
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrag !== null]);
  // Resize handlers - FIXED VERSION
  const handleResizeStart = (e: React.MouseEvent, eventId: string) => {
    e.preventDefault();
    e.stopPropagation();
    let currentY = e.clientY;
    let isResizing = true;
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizing) return;
      const deltaY = moveEvent.clientY - currentY;
      const minutesChange = Math.round(deltaY / 48 * 60); // 48px per hour
      const snappedChange = snapTo30Minutes(minutesChange);
      if (snappedChange === 0) return;
      setWeekData(prevData => {
        const newData = [...prevData];
        for (let dayIndex = 0; dayIndex < newData.length; dayIndex++) {
          const eventIndex = newData[dayIndex].events.findIndex(e => e.id === eventId);
          if (eventIndex !== -1) {
            const event = newData[dayIndex].events[eventIndex];
            if (!event.timeEnd) return prevData;
            const currentEndMinutes = timeToMinutes(event.timeEnd);
            const newEndMinutes = currentEndMinutes + snappedChange;
            const startMinutes = timeToMinutes(event.timeStart);
            // Minimum 30 minutes duration
            if (newEndMinutes - startMinutes < 30) return prevData;
            // Maximum 20:00 (1200 minutes)
            if (newEndMinutes > 1200) return prevData;
            const newEndTime = minutesToTime(newEndMinutes);
            // Check conflicts with checkin
            const checkin = newData[dayIndex].events.find(e => e.type === 'checkin');
            if (checkin && newEndMinutes > timeToMinutes(checkin.timeStart)) {
              return prevData;
            }
            newData[dayIndex] = {
              ...newData[dayIndex],
              events: newData[dayIndex].events.map(e => e.id === eventId ? {
                ...e,
                timeEnd: newEndTime
              } : e)
            };
            // Update currentY for next move
            currentY = moveEvent.clientY;
            break;
          }
        }
        return newData;
      });
    };
    const handleMouseUp = () => {
      isResizing = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setConfirmationMessage('Durata intervento modificata');
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  const toggleGoogleCalendar = (accountId: string, calendarId: string) => {
    setGoogleAccounts(googleAccounts.map(account => account.id === accountId ? {
      ...account,
      calendars: account.calendars.map(cal => cal.id === calendarId ? {
        ...cal,
        selected: !cal.selected
      } : cal)
    } : account));
  };
  const toggleOutlookCalendar = (accountId: string, calendarId: string) => {
    setOutlookAccounts(outlookAccounts.map(account => account.id === accountId ? {
      ...account,
      calendars: account.calendars.map(cal => cal.id === calendarId ? {
        ...cal,
        selected: !cal.selected
      } : cal)
    } : account));
  };
  // Helper to calculate vertical position based on day and horizontal position based on time
  const getPositionStyle = (dayIndex: number, timeStart: string, timeEnd?: string) => {
    const startHour = parseInt(timeStart.split(':')[0]);
    const startMin = parseInt(timeStart.split(':')[1]);
    const startTotalMinutes = (startHour - 8) * 60 + startMin;
    const topPercent = startTotalMinutes / 720 * 100;
    const leftPercent = dayIndex / 14 * 100; // Changed from 7 to 14
    const widthPercent = 100 / 14; // Changed from 7 to 14
    let heightPercent = 0;
    if (timeEnd) {
      const endHour = parseInt(timeEnd.split(':')[0]);
      const endMin = parseInt(timeEnd.split(':')[1]);
      const endTotalMinutes = (endHour - 8) * 60 + endMin;
      const durationMinutes = endTotalMinutes - startTotalMinutes;
      heightPercent = durationMinutes / 720 * 100;
    } else {
      heightPercent = 60 / 720 * 100;
    }
    return {
      left: `${Math.max(0, Math.min(100, leftPercent))}%`,
      width: `${Math.max(0, Math.min(100, widthPercent))}%`,
      top: `${Math.max(0, Math.min(100, topPercent))}%`,
      height: `${Math.max(0, Math.min(100, heightPercent))}%`
    };
  };
  const handleEventClick = (event: CalendarEvent) => {
    if (event.type === 'checkin' || event.type === 'checkout') {
      setSelectedBooking(event);
      setIsBookingModalOpen(true);
    }
  };
  const hours = Array.from({
    length: 13
  }, (_, i) => i + 8);
  return <MobileLayout data-id="element-1288">
      {/* Page Header - Updated with Integrations */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6 lg:mb-8" data-id="element-1289">
        <div data-id="element-1290">
          <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-1291">
            Pianificazione
          </h1>
        </div>

        {/* Right side: View switch (title height) + Integrations */}
        <div className="flex flex-col items-start lg:items-end gap-3" data-id="element-1292">
          {/* View Mode Switch - icon only, white bg with border */}
          <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-1293">
            <button onClick={() => setViewMode('list')} aria-label="Vista lista" aria-pressed={viewMode === 'list'} title="Vista lista" className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${viewMode === 'list' ? 'bg-coral text-white' : 'text-gray-500 hover:text-navy'}`} data-id="element-1294">
              <List size={18} data-id="element-1295" />
            </button>
            <button onClick={() => setViewMode('week')} aria-label="Vista calendario" aria-pressed={viewMode === 'week'} title="Vista calendario" className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${viewMode === 'week' ? 'bg-coral text-white' : 'text-gray-500 hover:text-navy'}`} data-id="element-1296">
              <CalendarIcon size={18} data-id="element-1297" />
            </button>
          </div>
          <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-1298">
            Integrazioni
          </span>
          <div className="flex items-center gap-2" data-id="element-1299">
            {/* Google Calendar Badge */}
            <button onClick={() => setIsGoogleModalOpen(true)} className={`relative flex items-center justify-center p-2 bg-white border rounded-lg transition-all ${integrations.google ? 'border-gray-200 hover:border-gray-300' : 'border-gray-200 hover:border-gray-300 opacity-40'}`} title={integrations.google ? '✓ Connesso a Google Calendar' : 'Clicca per connettere Google Calendar'} data-id="element-1300">
              <img src="https://cdn.magicpatterns.com/uploads/kH8QC7bV2biHRqFW7LmspQ/Google_Calendar_icon.png" alt="Google Calendar" className="w-5 h-5" data-id="element-1301" />
              <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${integrations.google ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-1302"></span>
            </button>

            {/* Outlook Badge */}
            <button onClick={() => setIsOutlookModalOpen(true)} className={`relative flex items-center justify-center p-2 bg-white border rounded-lg transition-all ${integrations.outlook ? 'border-gray-200 hover:border-gray-300' : 'border-gray-200 hover:border-gray-300 opacity-40'}`} title={integrations.outlook ? '✓ Connesso a Outlook' : 'Clicca per connettere Outlook'} data-id="element-1303">
              <img src="https://cdn.magicpatterns.com/uploads/mD3xBbUgdyBxcWhKYbGEK6/Microsoft_Office_Outlook.png" alt="Outlook" className="w-5 h-5" data-id="element-1304" />
              <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${integrations.outlook ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-1305"></span>
            </button>
          </div>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 lg:mb-6" data-id="element-1306">
        <div className="flex items-center gap-2 lg:gap-4" data-id="element-1307">
          <div className="flex items-center gap-1 lg:gap-2" data-id="element-1308">
            <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1309">
              <ChevronLeft size={20} data-id="element-1310" />
            </button>
            <span className="text-sm font-bold text-navy whitespace-nowrap" data-id="element-1311">
              15-28 Gen 2024
            </span>
            <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1312">
              <ChevronRight size={20} data-id="element-1313" />
            </button>
          </div>
          <button className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors min-h-[44px]" data-id="element-1314">
            Oggi
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-4" data-id="element-1315">
        <div className="flex-1 min-w-0" data-id="element-1316">
          {/* Gantt Chart Card - With drag & drop enabled */}
          {viewMode === 'week' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1317">
              <div className="overflow-x-auto" data-id="element-1318">
                <div className="min-w-[1800px]" data-id="element-1319">
                  {/* Days Header Row */}
                  <div className="flex border-b border-gray-200" data-id="element-1320">
                    <div className="w-20 flex-shrink-0 border-r border-gray-200 bg-gray-50 sticky left-0 z-20" data-id="element-1321"></div>
                    <div className="flex-1 flex bg-gray-50/50" data-id="element-1322">
                      {weekData.map(day => <div key={day.date} className="flex-1 h-10 border-l border-gray-200 flex items-center justify-center min-w-[120px]" data-id="element-1323">
                          <div className="flex items-center gap-2" data-id="element-1324">
                            <span className="text-xs text-gray-400 font-medium" data-id="element-1325">
                              {day.dayNumber}
                            </span>
                            <span className="text-xs text-gray-400 font-medium" data-id="element-1326">
                              {day.dayName}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Hours Rows with Events Container */}
                  <div className="relative" data-id="element-1327">
                    {/* Hours Rows */}
                    <div className="divide-y divide-gray-200 border-b border-gray-200" data-id="element-1328">
                      {hours.map(hour => <div key={hour} className="flex h-12 group hover:bg-gray-50/30 transition-colors" data-id="element-1329">
                          <div className="w-20 flex-shrink-0 border-r border-gray-200 flex items-center justify-center bg-gray-50 sticky left-0 z-20" data-id="element-1330">
                            <span className="text-xs text-gray-400 font-medium" data-id="element-1331">
                              {hour}:00
                            </span>
                          </div>
                          <div className="flex-1 relative" data-id="element-1332">
                            {weekData.map((_, dayIndex) => <div key={dayIndex} className="absolute top-0 bottom-0 border-l border-gray-200" style={{
                        left: `${dayIndex / 14 * 100}%`
                      }} data-id="element-1333" />)}
                          </div>
                        </div>)}
                    </div>

                    {/* Events Overlay - Pointer-based drag & drop */}
                    <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none" data-id="element-1334">
                      <div ref={timelineRef} className="ml-20 h-full relative pointer-events-auto" data-id="element-1335">
                        {/* Drop preview ghost while dragging */}
                        {activeDrag?.hasMoved && activeDrag.targetDayIndex !== null && activeDrag.targetStartMinutes !== null && <div className="absolute bg-[#FF4080]/20 border-2 border-[#FF4080] rounded-lg pointer-events-none z-20" style={{
                      left: `calc(${activeDrag.targetDayIndex / 14 * 100}% + 2px)`,
                      width: `calc(${100 / 14}% - 4px)`,
                      top: `${activeDrag.targetStartMinutes / 720 * 100}%`,
                      height: `${activeDrag.durationMinutes / 720 * 100}%`
                    }} data-id="element-1336" />}
                        {weekData.map((day, dayIndex) => <div key={day.date} className={`absolute top-0 bottom-0 transition-colors ${activeDrag?.hasMoved && activeDrag.targetDayIndex === dayIndex ? 'bg-[#FF4080]/5' : ''}`} style={{
                      left: `${dayIndex / 14 * 100}%`,
                      width: `${100 / 14}%`
                    }} data-id="element-1337">
                            {day.events.map(event => {
                        const style = getPositionStyle(dayIndex, event.timeStart, event.timeEnd);
                        const isTightWindow = event.label.includes('(Stretto!)');
                        {
                          /* External Events - Not draggable */
                        }
                        if (event.type === 'external') {
                          return <div key={event.id} className="absolute flex items-center justify-center bg-gray-100 border border-dashed border-gray-300 rounded-lg px-2 cursor-default z-1" style={{
                            left: '2px',
                            width: 'calc(100% - 4px)',
                            top: `calc(${style.top} + 2px)`,
                            height: `calc(${style.height} - 4px)`
                          }} title={`Evento da ${event.source === 'google' ? 'Google Calendar' : 'Outlook'}`} data-id="element-1338">
                                    <div className="flex flex-col items-center min-w-0" data-id="element-1339">
                                      <div className="flex items-center gap-1" data-id="element-1340">
                                        {event.source === 'google' ? <img src="https://cdn.magicpatterns.com/uploads/kH8QC7bV2biHRqFW7LmspQ/Google_Calendar_icon.png" alt="Google Calendar" className="w-3 h-3" data-id="element-1341" /> : <img src="https://cdn.magicpatterns.com/uploads/mD3xBbUgdyBxcWhKYbGEK6/Microsoft_Office_Outlook.png" alt="Outlook" className="w-3 h-3" data-id="element-1342" />}
                                        <span className="text-[10px] text-gray-500 font-medium" data-id="element-1343">
                                          {event.timeStart}
                                        </span>
                                      </div>
                                      <span className="text-[10px] text-gray-500 truncate" data-id="element-1344">
                                        {event.label}
                                      </span>
                                    </div>
                                  </div>;
                        }
                        {
                          /* Check-in/Check-out Events - Fixed, not draggable */
                        }
                        if (event.type === 'checkout' || event.type === 'checkin') {
                          const isCheckout = event.type === 'checkout';
                          return <div key={event.id} onClick={() => handleEventClick(event)} className="absolute flex items-center justify-center bg-white/90 border border-gray-200 rounded-lg px-2 shadow-sm hover:shadow-md hover:border-gray-300 transition-all cursor-pointer z-5" style={{
                            left: '2px',
                            width: 'calc(100% - 4px)',
                            top: `calc(${style.top} + 2px)`,
                            height: `calc(${style.height} - 4px)`
                          }} title="Evento fisso da Vikey" data-id="element-1345">
                                    <div className="flex flex-col items-center min-w-0" data-id="element-1346">
                                      <div className="flex items-center gap-1" data-id="element-1347">
                                        <Lock size={10} className="text-gray-400" data-id="element-1348" />
                                        <span className="text-[13px] font-bold text-gray-800 leading-tight" data-id="element-1349">
                                          {event.timeStart}
                                        </span>
                                      </div>
                                      <span className="text-xs text-gray-600 leading-tight truncate" data-id="element-1350">
                                        {isCheckout ? 'Check-out' : 'Check-in'}
                                      </span>
                                    </div>
                                  </div>;
                        }
                        {
                          /* Cleaning Events - Draggable & Resizable */
                        }
                        if (event.type === 'cleaning-suggested' || event.type === 'cleaning-confirmed') {
                          const isConfirmed = event.type === 'cleaning-confirmed';
                          const displayLabel = isTightWindow ? 'Pulizia' : event.label;
                          const timeRange = event.timeEnd ? `${event.timeStart}-${event.timeEnd}` : event.timeStart;
                          const eventDuration = event.timeEnd ? timeToMinutes(event.timeEnd) - timeToMinutes(event.timeStart) : 60;
                          return <div key={event.id} onPointerDown={e => handleEventPointerDown(e, event.id, dayIndex, eventDuration)} role="button" tabIndex={0} onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setIsWizardOpen(true);
                            }
                          }} className={`absolute flex flex-col justify-between bg-[#FFE6EF] border-2 border-dashed border-coral/30 rounded-lg px-2 py-1 cursor-grab active:cursor-grabbing z-10 hover:border-coral hover:shadow-md transition-all touch-none select-none ${draggedEvent === event.id ? 'opacity-40 ring-2 ring-coral/40' : ''}`} style={{
                            left: '2px',
                            width: 'calc(100% - 4px)',
                            top: `calc(${style.top} + 2px)`,
                            height: `calc(${style.height} - 4px)`
                          }} data-id="element-1351">
                                    <div className="flex flex-col min-w-0" data-id="element-1352">
                                      <div className="flex items-center gap-1" data-id="element-1353">
                                        <Sparkles size={10} className="text-coral flex-shrink-0" data-id="element-1354" />
                                        <span className="text-[11px] font-bold text-coral leading-tight truncate" data-id="element-1355">
                                          {event.timeStart}
                                          {event.timeEnd ? ` - ${event.timeEnd}` : ''}
                                        </span>
                                      </div>
                                      <span className="text-xs text-coral font-medium leading-tight truncate mt-0.5" data-id="element-1356">
                                        {event.label}
                                      </span>
                                    </div>
                                    <div className="h-2 w-full cursor-ns-resize flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" onMouseDown={e => handleResizeStart(e, event.id)} onPointerDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} data-id="element-1357">
                                      <div className="w-4 h-1 bg-coral/30 rounded-full" data-id="element-1358" />
                                    </div>
                                  </div>;
                        }
                        return null;
                      })}
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gantt Footer - Updated Legend */}
              <div className="border-t border-gray-200 bg-gray-50/50 px-4 lg:px-6 py-4" data-id="element-1359">
                <div className="flex flex-col gap-3" data-id="element-1360">
                  <div className="flex flex-wrap items-center gap-3 lg:gap-6" data-id="element-1361">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-1362">
                      Legenda:
                    </span>
                    <div className="flex items-center gap-2" data-id="element-1363">
                      <div className="w-3 h-3 bg-white border border-gray-200 rounded flex items-center justify-center" data-id="element-1364">
                        <Lock size={6} className="text-gray-400" data-id="element-1365" />
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-1366">Check-out</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1367">
                      <div className="w-3 h-3 bg-teal-50 border border-coral/30 rounded" data-id="element-1368"></div>
                      <span className="text-xs text-gray-600" data-id="element-1369">
                        Pulizia suggerita
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1370">
                      <div className="w-3 h-3 bg-green-50 border border-green-200 rounded" data-id="element-1371"></div>
                      <span className="text-xs text-gray-600" data-id="element-1372">Confermata</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1373">
                      <div className="w-3 h-3 bg-white border border-gray-200 rounded flex items-center justify-center" data-id="element-1374">
                        <Lock size={6} className="text-gray-400" data-id="element-1375" />
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-1376">Check-in</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1377">
                      <div className="w-3 h-3 bg-orange-50 border border-orange-300 rounded" data-id="element-1378"></div>
                      <span className="text-xs text-gray-600" data-id="element-1379">Stretta</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1380">
                      <div className="w-3 h-3 bg-gray-100 border border-dashed border-gray-300 rounded" data-id="element-1381"></div>
                      <span className="text-xs text-gray-600" data-id="element-1382">
                        Evento esterno
                      </span>
                    </div>
                  </div>

                  {/* Interaction hints */}
                  <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400" data-id="element-1383">
                    <span className="flex items-center gap-1" data-id="element-1384">
                      <Lock size={10} data-id="element-1385" /> = evento fisso
                    </span>
                    <span className="flex items-center gap-1" data-id="element-1386">
                      <GripVertical size={10} data-id="element-1387" /> = trascinabile
                    </span>
                    <span data-id="element-1388">
                      Gli interventi si spostano a intervalli di 30 minuti
                    </span>
                  </div>
                </div>
              </div>

              {/* Conflict Warning - Show when conflict detected */}
              {showConflictWarning && <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom duration-300" data-id="element-1389">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm shadow-lg" data-id="element-1390">
                    <AlertTriangle size={16} data-id="element-1391" />
                    <span data-id="element-1392">{conflictMessage}</span>
                  </div>
                </div>}

              {/* Confirmation Toast - Show after successful drag/resize */}
              {showConfirmation && <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom duration-300" data-id="element-1393">
                  <div className="bg-white shadow-lg rounded-lg p-4 border border-gray-200 flex items-center justify-between gap-4" data-id="element-1394">
                    <span className="text-sm text-gray-700" data-id="element-1395">
                      {confirmationMessage}
                    </span>
                    <button onClick={() => setShowConfirmation(false)} className="text-sm text-[#FF4080] hover:text-[#E63670] font-medium" data-id="element-1396">
                      OK
                    </button>
                  </div>
                </div>}
            </div>}

          {/* List View - Mobile-friendly */}
          {viewMode === 'list' && <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-5" data-id="element-1397">
              <div className="space-y-6" data-id="element-1398">
                {/* Da confermare section */}
                <div data-id="element-1399">
                  <h3 className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-4" data-id="element-1400">
                    Da confermare (10)
                  </h3>
                  <div className="space-y-4" data-id="element-1401">
                    {/* Intervention 1 - Lun 15 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1402">
                      <div className="flex items-center justify-between mb-4" data-id="element-1403">
                        <div className="text-sm font-bold text-navy" data-id="element-1404">
                          Lunedì 15 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1405">
                          <Check size={14} data-id="element-1406" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1407">
                        <button onClick={() => handleEventClick(weekData[0].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1408">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1409" />
                          <span className="text-sm font-bold text-navy" data-id="element-1410">
                            10:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1411">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1412">
                            (Roberto F.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1413">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1414"></div>
                          <span className="text-xs text-gray-400" data-id="element-1415">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1416">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1417"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1418">
                            11:00 - 15:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1419">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1420">(4h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1421">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1422"></div>
                          <span className="text-xs text-orange-500" data-id="element-1423">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[0].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1424">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1425" />
                          <span className="text-sm font-bold text-navy" data-id="element-1426">
                            15:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1427">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1428">
                            (Francesca N.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1429">
                        <div className="flex items-start gap-2" data-id="element-1430">
                          <span className="text-sm" data-id="element-1431">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1432">
                            <span className="font-medium text-gray-600" data-id="element-1433">
                              AI:
                            </span>{' '}
                            Inizio +1h per aerazione, durata 4h basata su 85m²
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 2 - Mar 16 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1434">
                      <div className="flex items-center justify-between mb-4" data-id="element-1435">
                        <div className="text-sm font-bold text-navy" data-id="element-1436">
                          Martedì 16 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1437">
                          <Check size={14} data-id="element-1438" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1439">
                        <button onClick={() => handleEventClick(weekData[1].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1440">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1441" />
                          <span className="text-sm font-bold text-navy" data-id="element-1442">
                            11:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1443">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1444">
                            (Luca P.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1445">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1446"></div>
                          <span className="text-xs text-gray-400" data-id="element-1447">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1448">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1449"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1450">
                            12:00 - 16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1451">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1452">(4h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1453">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1454"></div>
                          <span className="text-xs text-orange-500" data-id="element-1455">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[1].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1456">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1457" />
                          <span className="text-sm font-bold text-navy" data-id="element-1458">
                            16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1459">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1460">
                            (Giulia S.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1461">
                        <div className="flex items-start gap-2" data-id="element-1462">
                          <span className="text-sm" data-id="element-1463">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1464">
                            <span className="font-medium text-gray-600" data-id="element-1465">
                              AI:
                            </span>{' '}
                            Ampia finestra di 5h disponibile. Programmato inizio
                            alle 12:00 per ottimizzare i turni.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 3 - Gio 18 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1466">
                      <div className="flex items-center justify-between mb-4" data-id="element-1467">
                        <div className="text-sm font-bold text-navy" data-id="element-1468">
                          Giovedì 18 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1469">
                          <Check size={14} data-id="element-1470" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1471">
                        <button onClick={() => handleEventClick(weekData[3].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1472">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1473" />
                          <span className="text-sm font-bold text-navy" data-id="element-1474">
                            10:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1475">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1476">
                            (Francesca N.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1477">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1478"></div>
                          <span className="text-xs text-gray-400" data-id="element-1479">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1480">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1481"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1482">
                            11:00 - 14:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1483">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1484">(3h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1485">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1486"></div>
                          <span className="text-xs text-orange-500" data-id="element-1487">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[3].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1488">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1489" />
                          <span className="text-sm font-bold text-navy" data-id="element-1490">
                            14:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1491">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1492">
                            (Davide C.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1493">
                        <div className="flex items-start gap-2" data-id="element-1494">
                          <span className="text-sm" data-id="element-1495">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1496">
                            <span className="font-medium text-gray-600" data-id="element-1497">
                              AI:
                            </span>{' '}
                            Durata 3h ottimizzata per metratura standard.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 4 - Ven 19 Gen (Tight window) */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4 border-l-2 border-l-orange-400" data-id="element-1498">
                      <div className="flex items-center justify-between mb-4" data-id="element-1499">
                        <div className="flex items-center gap-2" data-id="element-1500">
                          <div className="text-sm font-bold text-navy" data-id="element-1501">
                            Venerdì 19 Gennaio
                          </div>
                          <span className="inline-flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full font-medium" data-id="element-1502">
                            <AlertTriangle size={12} data-id="element-1503" />
                            Finestra stretta
                          </span>
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1504">
                          <Check size={14} data-id="element-1505" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1506">
                        <button onClick={() => handleEventClick(weekData[4].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1507">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1508" />
                          <span className="text-sm font-bold text-navy" data-id="element-1509">
                            11:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1510">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1511">
                            (Sofia L.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1512">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1513"></div>
                          <span className="text-xs text-gray-400" data-id="element-1514">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1515">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1516"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1517">
                            12:00 - 14:30
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1518">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1519">
                            (2h 30min)
                          </span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1520">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1521"></div>
                          <span className="text-xs text-orange-500" data-id="element-1522">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[4].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1523">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1524" />
                          <span className="text-sm font-bold text-navy" data-id="element-1525">
                            14:30
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1526">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1527">
                            (Paolo V.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1528">
                        <div className="flex items-start gap-2" data-id="element-1529">
                          <span className="text-sm" data-id="element-1530">💡</span>
                          <p className="text-xs text-orange-600" data-id="element-1531">
                            <span className="font-medium text-orange-700" data-id="element-1532">
                              AI:
                            </span>{' '}
                            ⚠️ Finestra stretta: solo 2h 30min disponibili.
                            Consigliato team di 2 persone per completare in
                            tempo.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 5 - Lun 22 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1533">
                      <div className="flex items-center justify-between mb-4" data-id="element-1534">
                        <div className="text-sm font-bold text-navy" data-id="element-1535">
                          Lunedì 22 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1536">
                          <Check size={14} data-id="element-1537" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1538">
                        <button onClick={() => handleEventClick(weekData[6].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1539">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1540" />
                          <span className="text-sm font-bold text-navy" data-id="element-1541">
                            11:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1542">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1543">
                            (Paolo V.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1544">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1545"></div>
                          <span className="text-xs text-gray-400" data-id="element-1546">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1547">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1548"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1549">
                            12:00 - 15:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1550">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1551">(3h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1552">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1553"></div>
                          <span className="text-xs text-orange-500" data-id="element-1554">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[6].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1555">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1556" />
                          <span className="text-sm font-bold text-navy" data-id="element-1557">
                            15:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1558">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1559">
                            (Chiara B.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1560">
                        <div className="flex items-start gap-2" data-id="element-1561">
                          <span className="text-sm" data-id="element-1562">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1563">
                            <span className="font-medium text-gray-600" data-id="element-1564">
                              AI:
                            </span>{' '}
                            Durata 3h ottimizzata per metratura standard.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 6 - Mar 23 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1565">
                      <div className="flex items-center justify-between mb-4" data-id="element-1566">
                        <div className="text-sm font-bold text-navy" data-id="element-1567">
                          Martedì 23 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1568">
                          <Check size={14} data-id="element-1569" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1570">
                        <button onClick={() => handleEventClick(weekData[7].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1571">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1572" />
                          <span className="text-sm font-bold text-navy" data-id="element-1573">
                            10:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1574">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1575">
                            (Davide C.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1576">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1577"></div>
                          <span className="text-xs text-gray-400" data-id="element-1578">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1579">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1580"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1581">
                            11:00 - 16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1582">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1583">(5h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1584">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1585"></div>
                          <span className="text-xs text-orange-500" data-id="element-1586">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[7].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1587">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1588" />
                          <span className="text-sm font-bold text-navy" data-id="element-1589">
                            16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1590">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1591">
                            (Valentina R.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1592">
                        <div className="flex items-start gap-2" data-id="element-1593">
                          <span className="text-sm" data-id="element-1594">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1595">
                            <span className="font-medium text-gray-600" data-id="element-1596">
                              AI:
                            </span>{' '}
                            Ampia finestra di 5h disponibile. Programmato inizio
                            alle 11:00 per ottimizzare i turni.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 7 - Gio 25 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1597">
                      <div className="flex items-center justify-between mb-4" data-id="element-1598">
                        <div className="text-sm font-bold text-navy" data-id="element-1599">
                          Giovedì 25 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1600">
                          <Check size={14} data-id="element-1601" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1602">
                        <button onClick={() => handleEventClick(weekData[8].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1603">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1604" />
                          <span className="text-sm font-bold text-navy" data-id="element-1605">
                            11:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1606">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1607">
                            (Elena T.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1608">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1609"></div>
                          <span className="text-xs text-gray-400" data-id="element-1610">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1611">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1612"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1613">
                            12:00 - 14:30
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1614">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1615">
                            (2h 30min)
                          </span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1616">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1617"></div>
                          <span className="text-xs text-orange-500" data-id="element-1618">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[8].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1619">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1620" />
                          <span className="text-sm font-bold text-navy" data-id="element-1621">
                            14:30
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1622">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1623">
                            (Simone G.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1624">
                        <div className="flex items-start gap-2" data-id="element-1625">
                          <span className="text-sm" data-id="element-1626">💡</span>
                          <p className="text-xs text-orange-600" data-id="element-1627">
                            <span className="font-medium text-orange-700" data-id="element-1628">
                              AI:
                            </span>{' '}
                            ⚠️ Finestra stretta: solo 2h 30min disponibili.
                            Consigliato team di 2 persone per completare in
                            tempo.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 8 - Ven 26 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1629">
                      <div className="flex items-center justify-between mb-4" data-id="element-1630">
                        <div className="text-sm font-bold text-navy" data-id="element-1631">
                          Venerdì 26 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1632">
                          <Check size={14} data-id="element-1633" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1634">
                        <button onClick={() => handleEventClick(weekData[9].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1635">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1636" />
                          <span className="text-sm font-bold text-navy" data-id="element-1637">
                            10:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1638">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1639">
                            (Andrea M.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1640">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1641"></div>
                          <span className="text-xs text-gray-400" data-id="element-1642">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1643">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1644"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1645">
                            11:00 - 15:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1646">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1647">(4h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1648">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1649"></div>
                          <span className="text-xs text-orange-500" data-id="element-1650">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[9].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1651">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1652" />
                          <span className="text-sm font-bold text-navy" data-id="element-1653">
                            15:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1654">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1655">
                            (Martina L.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1656">
                        <div className="flex items-start gap-2" data-id="element-1657">
                          <span className="text-sm" data-id="element-1658">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1659">
                            <span className="font-medium text-gray-600" data-id="element-1660">
                              AI:
                            </span>{' '}
                            Durata 4h ottimizzata per metratura standard.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 9 - Sab 27 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1661">
                      <div className="flex items-center justify-between mb-4" data-id="element-1662">
                        <div className="text-sm font-bold text-navy" data-id="element-1663">
                          Sabato 27 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1664">
                          <Check size={14} data-id="element-1665" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1666">
                        <button onClick={() => handleEventClick(weekData[10].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1667">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1668" />
                          <span className="text-sm font-bold text-navy" data-id="element-1669">
                            11:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1670">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1671">
                            (Laura M.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1672">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1673"></div>
                          <span className="text-xs text-gray-400" data-id="element-1674">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1675">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1676"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1677">
                            12:00 - 16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1678">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1679">(4h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1680">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1681"></div>
                          <span className="text-xs text-orange-500" data-id="element-1682">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[10].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1683">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1684" />
                          <span className="text-sm font-bold text-navy" data-id="element-1685">
                            16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1686">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1687">
                            (Giuseppe P.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1688">
                        <div className="flex items-start gap-2" data-id="element-1689">
                          <span className="text-sm" data-id="element-1690">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1691">
                            <span className="font-medium text-gray-600" data-id="element-1692">
                              AI:
                            </span>{' '}
                            Durata 4h ottimizzata per metratura standard.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Intervention 10 - Dom 28 Gen */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1693">
                      <div className="flex items-center justify-between mb-4" data-id="element-1694">
                        <div className="text-sm font-bold text-navy" data-id="element-1695">
                          Domenica 28 Gennaio
                        </div>
                        <button className="px-4 py-2 bg-[#FF4080] text-white rounded-full text-sm font-medium hover:bg-[#E63670] flex items-center gap-1 min-h-[40px]" data-id="element-1696">
                          <Check size={14} data-id="element-1697" />
                          Conferma
                        </button>
                      </div>
                      <div className="space-y-1" data-id="element-1698">
                        <button onClick={() => handleEventClick(weekData[11].events[0])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1699">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1700" />
                          <span className="text-sm font-bold text-navy" data-id="element-1701">
                            11:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1702">
                            Check-out
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1703">
                            (Chiara B.)
                          </span>
                        </button>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1704">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1705"></div>
                          <span className="text-xs text-gray-400" data-id="element-1706">
                            ↓ 1h di margine
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1707">
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-deep" data-id="element-1708"></div>
                          <span className="text-sm font-medium text-teal-deep" data-id="element-1709">
                            12:00 - 16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1710">Pulizia</span>
                          <span className="text-xs text-gray-400" data-id="element-1711">(4h)</span>
                        </div>
                        <div className="flex items-center gap-2 ml-0.5 pl-px" data-id="element-1712">
                          <div className="w-px h-3 bg-gray-200" data-id="element-1713"></div>
                          <span className="text-xs text-orange-500" data-id="element-1714">
                            ↓ 0 margine
                          </span>
                        </div>
                        <button onClick={() => handleEventClick(weekData[11].events[2])} className="inline-flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300 hover:bg-gray-100 transition-all cursor-pointer" data-id="element-1715">
                          <Lock size={12} className="text-gray-400 flex-shrink-0" data-id="element-1716" />
                          <span className="text-sm font-bold text-navy" data-id="element-1717">
                            16:00
                          </span>
                          <span className="text-sm text-gray-600" data-id="element-1718">
                            Check-in
                          </span>
                          <span className="text-sm text-gray-400" data-id="element-1719">
                            (Federico T.)
                          </span>
                        </button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1720">
                        <div className="flex items-start gap-2" data-id="element-1721">
                          <span className="text-sm" data-id="element-1722">💡</span>
                          <p className="text-xs text-gray-500" data-id="element-1723">
                            <span className="font-medium text-gray-600" data-id="element-1724">
                              AI:
                            </span>{' '}
                            Durata 4h ottimizzata per metratura standard.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Confermate section */}
                <div data-id="element-1725">
                  <h3 className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-4" data-id="element-1726">
                    Confermate
                  </h3>
                  <div className="text-center py-8" data-id="element-1727">
                    <p className="text-sm text-gray-400" data-id="element-1728">
                      Nessun intervento confermato in questo periodo
                    </p>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* Google Calendar Modal */}
      {isGoogleModalOpen && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={() => setIsGoogleModalOpen(false)} data-id="element-1729">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-1730">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-1731">
              <div className="flex items-center gap-3" data-id="element-1732">
                <img src="https://cdn.magicpatterns.com/uploads/kH8QC7bV2biHRqFW7LmspQ/Google_Calendar_icon.png" alt="Google Calendar" className="w-8 h-8" data-id="element-1733" />
                <div data-id="element-1734">
                  <h2 className="text-lg lg:text-xl font-bold text-navy" data-id="element-1735">
                    Google Calendar
                  </h2>
                  <p className="text-xs lg:text-sm text-gray-500 mt-0.5" data-id="element-1736">
                    Gestisci account e calendari Google
                  </p>
                </div>
              </div>
              <button onClick={() => setIsGoogleModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1737">
                <X size={20} className="text-gray-500" data-id="element-1738" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-1739">
              <div className="space-y-6" data-id="element-1740">
                {/* Connected Accounts */}
                {googleAccounts.length > 0 ? <div className="space-y-4" data-id="element-1741">
                    {googleAccounts.map(account => <div key={account.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1742">
                        <div className="flex items-center justify-between mb-4" data-id="element-1743">
                          <div className="flex items-center gap-2" data-id="element-1744">
                            <User size={16} className="text-gray-400" data-id="element-1745" />
                            <span className="text-sm font-semibold text-navy" data-id="element-1746">
                              {account.email}
                            </span>
                          </div>
                          <button className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" data-id="element-1747">
                            <X size={16} data-id="element-1748" />
                          </button>
                        </div>

                        {/* Calendars List with Checkboxes */}
                        <div className="space-y-2" data-id="element-1749">
                          <p className="text-xs text-gray-500 font-medium mb-2" data-id="element-1750">
                            Seleziona calendari da sincronizzare:
                          </p>
                          {account.calendars.map(calendar => <label key={calendar.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors" data-id="element-1751">
                              <input type="checkbox" checked={calendar.selected} onChange={() => toggleGoogleCalendar(account.id, calendar.id)} className="w-4 h-4 text-teal-deep border-gray-300 rounded focus:ring-teal-deep focus:ring-2" data-id="element-1752" />
                              <span className="text-sm text-gray-700" data-id="element-1753">
                                {calendar.name}
                              </span>
                            </label>)}
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-8 text-sm text-gray-500" data-id="element-1754">
                    Nessun account Google connesso
                  </div>}

                {/* Add Account Button */}
                <button className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-deep hover:bg-teal-50/30 transition-all flex items-center justify-center gap-2 text-gray-600 hover:text-teal-deep min-h-[44px]" data-id="element-1755">
                  <Plus size={18} data-id="element-1756" />
                  <span className="text-sm font-medium" data-id="element-1757">
                    Aggiungi altro account Google
                  </span>
                </button>

                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-1758">
                  <CalendarIcon size={18} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1759" />
                  <div data-id="element-1760">
                    <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-1761">
                      Sincronizzazione automatica
                    </p>
                    <p className="text-xs text-blue-700" data-id="element-1762">
                      Solo i calendari selezionati verranno sincronizzati con
                      CleanAi per evitare sovrapposizioni negli interventi.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 lg:p-6" data-id="element-1763">
              <button onClick={() => setIsGoogleModalOpen(false)} className="w-full px-4 py-2.5 bg-[#FF4080] text-white rounded-full hover:bg-[#E63670] transition-colors font-medium text-sm min-h-[44px]" data-id="element-1764">
                Salva e Chiudi
              </button>
            </div>
          </div>
        </div>}

      {/* Outlook Calendar Modal */}
      {isOutlookModalOpen && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={() => setIsOutlookModalOpen(false)} data-id="element-1765">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-1766">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-1767">
              <div className="flex items-center gap-3" data-id="element-1768">
                <img src="https://cdn.magicpatterns.com/uploads/mD3xBbUgdyBxcWhKYbGEK6/Microsoft_Office_Outlook.png" alt="Outlook" className="w-8 h-8" data-id="element-1769" />
                <div data-id="element-1770">
                  <h2 className="text-lg lg:text-xl font-bold text-navy" data-id="element-1771">
                    Outlook Calendar
                  </h2>
                  <p className="text-xs lg:text-sm text-gray-500 mt-0.5" data-id="element-1772">
                    Gestisci account e calendari Outlook
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOutlookModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1773">
                <X size={20} className="text-gray-500" data-id="element-1774" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-1775">
              <div className="space-y-6" data-id="element-1776">
                {/* Connected Accounts */}
                {integrations.outlook && outlookAccounts.length > 0 ? <div className="space-y-4" data-id="element-1777">
                    {outlookAccounts.map(account => <div key={account.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1778">
                        <div className="flex items-center justify-between mb-4" data-id="element-1779">
                          <div className="flex items-center gap-2" data-id="element-1780">
                            <User size={16} className="text-gray-400" data-id="element-1781" />
                            <span className="text-sm font-semibold text-navy" data-id="element-1782">
                              {account.email}
                            </span>
                          </div>
                          <button className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" data-id="element-1783">
                            <X size={16} data-id="element-1784" />
                          </button>
                        </div>

                        {/* Calendars List with Checkboxes */}
                        <div className="space-y-2" data-id="element-1785">
                          <p className="text-xs text-gray-500 font-medium mb-2" data-id="element-1786">
                            Seleziona calendari da sincronizzare:
                          </p>
                          {account.calendars.map(calendar => <label key={calendar.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors" data-id="element-1787">
                              <input type="checkbox" checked={calendar.selected} onChange={() => toggleOutlookCalendar(account.id, calendar.id)} className="w-4 h-4 text-teal-deep border-gray-300 rounded focus:ring-teal-deep focus:ring-2" data-id="element-1788" />
                              <span className="text-sm text-gray-700" data-id="element-1789">
                                {calendar.name}
                              </span>
                            </label>)}
                        </div>
                      </div>)}
                  </div> : <div className="text-center py-8" data-id="element-1790">
                    <p className="text-sm text-gray-500 mb-4" data-id="element-1791">
                      Nessun account Outlook connesso
                    </p>
                    <button className="px-4 py-2.5 bg-[#FF4080] text-white rounded-full hover:bg-[#E63670] transition-colors font-medium text-sm min-h-[44px]" data-id="element-1792">
                      Connetti Outlook
                    </button>
                  </div>}

                {/* Add Account Button - Only show if already connected */}
                {integrations.outlook && <button className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-teal-deep hover:bg-teal-50/30 transition-all flex items-center justify-center gap-2 text-gray-600 hover:text-teal-deep min-h-[44px]" data-id="element-1793">
                    <Plus size={18} data-id="element-1794" />
                    <span className="text-sm font-medium" data-id="element-1795">
                      Aggiungi altro account Outlook
                    </span>
                  </button>}

                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-1796">
                  <CalendarIcon size={18} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1797" />
                  <div data-id="element-1798">
                    <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-1799">
                      Sincronizzazione automatica
                    </p>
                    <p className="text-xs text-blue-700" data-id="element-1800">
                      Solo i calendari selezionati verranno sincronizzati con
                      CleanAi per evitare sovrapposizioni negli interventi.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 lg:p-6" data-id="element-1801">
              <button onClick={() => setIsOutlookModalOpen(false)} className="w-full px-4 py-2.5 bg-[#FF4080] text-white rounded-full hover:bg-[#E63670] transition-colors font-medium text-sm min-h-[44px]" data-id="element-1802">
                {integrations.outlook ? 'Salva e Chiudi' : 'Chiudi'}
              </button>
            </div>
          </div>
        </div>}

      {/* Booking Details Modal - Full screen on mobile */}
      {isBookingModalOpen && selectedBooking && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-0 lg:p-4 animate-in fade-in duration-200" onClick={() => setIsBookingModalOpen(false)} data-id="element-1803">
          <div className="bg-white lg:rounded-xl shadow-2xl w-full h-full lg:h-[90vh] lg:max-w-5xl flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-1804">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-1805">
              <div data-id="element-1806">
                <h2 className="text-lg lg:text-xl font-bold text-navy" data-id="element-1807">
                  Dettagli Prenotazione
                </h2>
                <p className="text-xs lg:text-sm text-gray-500 mt-1" data-id="element-1808">
                  ID: #VK-2024-001234
                </p>
              </div>
              <button onClick={() => setIsBookingModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" data-id="element-1809">
                <X size={20} className="text-gray-500" data-id="element-1810" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-1811">
              <div className="max-w-4xl mx-auto space-y-4 lg:space-y-6" data-id="element-1812">
                {/* Booking Status Banner */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 lg:p-4 flex items-center gap-3" data-id="element-1813">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-1814">
                    <CalendarIcon className="text-green-600" size={20} data-id="element-1815" />
                  </div>
                  <div data-id="element-1816">
                    <p className="font-semibold text-green-900 text-sm lg:text-base" data-id="element-1817">
                      Prenotazione Confermata
                    </p>
                    <p className="text-xs lg:text-sm text-green-700" data-id="element-1818">
                      {selectedBooking.type === 'checkin' ? 'Check-in' : 'Check-out'}
                      : {selectedBooking.timeStart}
                    </p>
                  </div>
                </div>

                {/* Guest Information */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6" data-id="element-1819">
                  <h3 className="text-base lg:text-lg font-bold text-navy mb-3 lg:mb-4 flex items-center gap-2" data-id="element-1820">
                    <User size={18} data-id="element-1821" />
                    Informazioni Ospite
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6" data-id="element-1822">
                    <div data-id="element-1823">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1824">
                        Nome Completo
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1825">
                        <User size={16} className="text-gray-400" data-id="element-1826" />
                        <span className="text-navy font-medium text-sm" data-id="element-1827">
                          {selectedBooking.label}
                        </span>
                      </div>
                    </div>
                    <div data-id="element-1828">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1829">
                        Email
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1830">
                        <Mail size={16} className="text-gray-400" data-id="element-1831" />
                        <span className="text-navy text-sm break-all" data-id="element-1832">
                          ospite@email.com
                        </span>
                      </div>
                    </div>
                    <div data-id="element-1833">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1834">
                        Telefono
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1835">
                        <Phone size={16} className="text-gray-400" data-id="element-1836" />
                        <span className="text-navy text-sm" data-id="element-1837">
                          +39 348 123 4567
                        </span>
                      </div>
                    </div>
                    <div data-id="element-1838">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1839">
                        Numero Ospiti
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1840">
                        <Users size={16} className="text-gray-400" data-id="element-1841" />
                        <span className="text-navy text-sm" data-id="element-1842">2 adulti</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Property & Dates */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6" data-id="element-1843">
                  <h3 className="text-base lg:text-lg font-bold text-navy mb-3 lg:mb-4 flex items-center gap-2" data-id="element-1844">
                    <Home size={18} data-id="element-1845" />
                    Dettagli Soggiorno
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6" data-id="element-1846">
                    <div data-id="element-1847">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1848">
                        Proprietà
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1849">
                        <MapPin size={16} className="text-gray-400 flex-shrink-0" data-id="element-1850" />
                        <div data-id="element-1851">
                          <p className="text-navy font-medium text-sm" data-id="element-1852">
                            Villa Sunset
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1853">
                            Via dei Giardini 12, Milano
                          </p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-1854">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1855">
                        Periodo
                      </label>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-1856">
                        <CalendarIcon size={16} className="text-gray-400 flex-shrink-0" data-id="element-1857" />
                        <div data-id="element-1858">
                          <p className="text-navy font-medium text-sm" data-id="element-1859">
                            18 Gen - 22 Gen 2024
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1860">4 notti</p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-1861">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1862">
                        Check-in
                      </label>
                      <div className="p-3 bg-teal-50 rounded-lg border border-coral/30" data-id="element-1863">
                        <p className="text-teal-deep font-semibold text-sm" data-id="element-1864">
                          {selectedBooking.type === 'checkin' ? selectedBooking.timeStart : '14:00'}
                        </p>
                      </div>
                    </div>
                    <div data-id="element-1865">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-1866">
                        Check-out
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg" data-id="element-1867">
                        <p className="text-navy font-semibold text-sm" data-id="element-1868">
                          {selectedBooking.type === 'checkout' ? selectedBooking.timeStart : '11:00'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-6" data-id="element-1869">
                  <h3 className="text-base lg:text-lg font-bold text-navy mb-3 lg:mb-4 flex items-center gap-2" data-id="element-1870">
                    <CreditCard size={18} data-id="element-1871" />
                    Informazioni Pagamento
                  </h3>
                  <div className="space-y-3 lg:space-y-4" data-id="element-1872">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg" data-id="element-1873">
                      <span className="text-gray-600 text-sm" data-id="element-1874">
                        Tariffa notte (x4)
                      </span>
                      <span className="text-navy font-medium text-sm" data-id="element-1875">
                        €800,00
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg" data-id="element-1876">
                      <span className="text-gray-600 text-sm" data-id="element-1877">
                        Pulizie finali
                      </span>
                      <span className="text-navy font-medium text-sm" data-id="element-1878">
                        €80,00
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 lg:p-4 bg-teal-50 rounded-lg border-2 border-coral/30" data-id="element-1879">
                      <span className="text-teal-deep font-bold text-sm lg:text-base" data-id="element-1880">
                        Totale Pagato
                      </span>
                      <span className="text-teal-deep font-bold text-lg lg:text-xl" data-id="element-1881">
                        €880,00
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs lg:text-sm text-green-700 bg-green-50 p-3 rounded-lg" data-id="element-1882">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100" data-id="element-1883">
                        PAGATO
                      </span>
                      <span data-id="element-1884">Pagamento ricevuto il 10 Gen 2024</span>
                    </div>
                  </div>
                </div>

                {/* Integration Badge */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-400 italic py-4" data-id="element-1885">
                  <ExternalLink size={14} data-id="element-1886" />
                  <span data-id="element-1887">Dati sincronizzati con Vickey</span>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Nuovo intervento wizard — opened by clicking a suggested cleaning slot */}
      <NuovoInterventoWizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} data-id="element-1888" />
    </MobileLayout>;
}